import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

/**
 * Local persistence for watch history, saved videos and followed channels.
 *
 * Two backends, one synchronous API:
 *
 *   - Web keeps using localStorage directly, exactly as before.
 *   - Native (Capacitor) uses Preferences, which is real device storage. The web
 *     view's own localStorage is not safe to rely on there: WKWebView evicts it
 *     under storage pressure, and a visitor would silently lose everything they
 *     had saved with no error and nothing to recover from.
 *
 * Preferences is async, but every caller here - `isSaved(id)` inside a useState
 * initialiser, for instance - is synchronous. So on native the values are read
 * once into an in-memory cache at startup (see hydrateStorage) and served from
 * there; writes update the cache immediately and persist in the background. That
 * keeps all the existing call sites working untouched.
 */

const HISTORY_KEY = "tz.history";
const SAVED_KEY = "tz.saved";
const FOLLOWS_KEY = "tz.follows";
const HISTORY_LIMIT = 200;

// The session token and the chosen region live under the same storage and would be
// lost in the same way - being silently signed out is as bad as losing a library.
export const TOKEN_KEY = "tz.userToken";
export const COUNTRY_KEY = "tz.country";

const ALL_KEYS = [HISTORY_KEY, SAVED_KEY, FOLLOWS_KEY, TOKEN_KEY, COUNTRY_KEY];

const isNative = Capacitor.isNativePlatform();

// Only used on native. On web, localStorage is already a synchronous store.
const cache = new Map();
let hydrated = !isNative;

/**
 * Load native storage into memory. Resolves immediately on web.
 *
 * Must finish before the first render on native, otherwise the initial read
 * returns empty and the UI briefly claims nothing is saved.
 */
// Values written through readRaw/writeRaw are stored verbatim: the token is a bare
// string, and the country is written as an already-encoded JSON string by its
// caller. Hydration must not parse either, or the raw read would hand back an
// object the caller then tries to JSON.parse a second time.
const RAW_KEYS = new Set([TOKEN_KEY, COUNTRY_KEY]);

export const hydrateStorage = async () => {
  if (hydrated) return;
  await Promise.all(
    ALL_KEYS.map(async (key) => {
      try {
        const { value } = await Preferences.get({ key });
        if (value === null || value === undefined) return;
        cache.set(key, RAW_KEYS.has(key) ? value : JSON.parse(value));
      } catch {
        /* first run, or a corrupt value - fall back to the empty default */
      }
    })
  );
  hydrated = true;
};

/** Read a plain string value (no JSON), for things like the session token. */
export const readRaw = (key) => {
  if (isNative) return cache.has(key) ? cache.get(key) : null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/** Write, or remove when the value is null. */
export const writeRaw = (key, value) => {
  try {
    if (isNative) {
      if (value === null || value === undefined) {
        cache.delete(key);
        Preferences.remove({ key }).catch(() => {});
      } else {
        cache.set(key, value);
        Preferences.set({ key, value }).catch(() => {});
      }
      return;
    }
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage blocked - the in-memory state still applies for this session */
  }
};

const read = (key, fallback) => {
  if (isNative) {
    return cache.has(key) ? cache.get(key) : fallback;
  }
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    // Corrupt or unavailable storage (private mode, quota) must never break the page.
    return fallback;
  }
};

const write = (key, value) => {
  try {
    if (isNative) {
      // Cache first so the very next synchronous read is already correct; the
      // device write is allowed to land a tick later.
      cache.set(key, value);
      Preferences.set({ key, value: JSON.stringify(value) }).catch(() => {});
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
    // Same-tab listeners: the native "storage" event only fires in *other* tabs.
    window.dispatchEvent(new CustomEvent("tz-storage", { detail: { key } }));
  } catch {
    /* storage full or blocked - the in-memory state still updated */
  }
};

const slim = (video) => ({
  id: video.id,
  title: video.title,
  channel_title: video.channel_title,
  channel_id: video.channel_id,
  thumbnail_url: video.thumbnail_url,
  duration: video.duration,
  view_count: video.view_count,
  platform: video.platform,
  category_id: video.category_id,
  language: video.language,
});

export const recordWatch = (video) => {
  if (!video?.id) return;
  const history = read(HISTORY_KEY, []);
  const next = [
    { ...slim(video), watched_at: new Date().toISOString() },
    ...history.filter((v) => v.id !== video.id),
  ].slice(0, HISTORY_LIMIT);
  write(HISTORY_KEY, next);
};

export const getHistory = () => read(HISTORY_KEY, []);
export const clearHistory = () => write(HISTORY_KEY, []);

export const getSaved = () => read(SAVED_KEY, []);

export const isSaved = (id) => read(SAVED_KEY, []).some((v) => v.id === id);

export const toggleSaved = (video) => {
  const saved = read(SAVED_KEY, []);
  const exists = saved.some((v) => v.id === video.id);
  const next = exists
    ? saved.filter((v) => v.id !== video.id)
    : [{ ...slim(video), saved_at: new Date().toISOString() }, ...saved];
  write(SAVED_KEY, next);
  return !exists;
};

/**
 * Followed channels.
 *
 * Same local-first rule as saves: this works with no account, and an account only
 * copies it to the server so a second device sees the same list.
 */
export const getFollows = () => read(FOLLOWS_KEY, []);

export const isFollowing = (channelId) =>
  read(FOLLOWS_KEY, []).some((c) => c.channel_id === channelId);

export const toggleFollow = (channel) => {
  const follows = read(FOLLOWS_KEY, []);
  const exists = follows.some((c) => c.channel_id === channel.channel_id);
  const next = exists
    ? follows.filter((c) => c.channel_id !== channel.channel_id)
    : [{ ...channel, followed_at: new Date().toISOString() }, ...follows];
  write(FOLLOWS_KEY, next);
  return !exists;
};

/**
 * Overwrite the local library with a server copy.
 *
 * Called only after a merge, so this widens the local list rather than trimming it.
 */
export const replaceLibrary = ({ saved, history, follows }) => {
  if (Array.isArray(saved)) write(SAVED_KEY, saved.slice(0, HISTORY_LIMIT * 2));
  if (Array.isArray(history)) write(HISTORY_KEY, history.slice(0, HISTORY_LIMIT));
  if (Array.isArray(follows)) write(FOLLOWS_KEY, follows);
};

/**
 * Wipe the on-device library.
 *
 * Only the delete-account flow calls this, and only when the visitor explicitly
 * asks for it. Deleting a server account deliberately leaves the local copy
 * alone otherwise - the library works signed out, so wiping it silently would
 * take away something the account was never holding.
 *
 * The token goes too: whatever it pointed at no longer exists.
 */
export const clearLocalLibrary = () => {
  write(HISTORY_KEY, []);
  write(SAVED_KEY, []);
  write(FOLLOWS_KEY, []);
  writeRaw(TOKEN_KEY, null);
};

const KEYS_BY_NAME = {
  saved: SAVED_KEY,
  history: HISTORY_KEY,
  follows: FOLLOWS_KEY,
};

/** Re-renders whenever the given list changes, in this tab or another. */
export const useStoredList = (which) => {
  const key = KEYS_BY_NAME[which] || HISTORY_KEY;
  const [items, setItems] = useState(() => read(key, []));

  const refresh = useCallback(() => setItems(read(key, [])), [key]);

  useEffect(() => {
    const onChange = (e) => {
      if (!e.detail || e.detail.key === key) refresh();
    };
    window.addEventListener("tz-storage", onChange);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("tz-storage", onChange);
      window.removeEventListener("storage", refresh);
    };
  }, [key, refresh]);

  return [items, refresh];
};

/**
 * Categories and languages the visitor actually watches, most-watched first.
 *
 * This is what "For You" is built from - no profile, no tracking, just the last
 * few dozen things they opened.
 */
export const getTasteProfile = () => {
  const history = read(HISTORY_KEY, []).slice(0, 60);
  const tally = (field) =>
    Object.entries(
      history.reduce((acc, v) => {
        const key = v[field];
        if (key) acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {})
    )
      .sort((a, b) => b[1] - a[1])
      .map(([value]) => value);

  return {
    categories: tally("category_id"),
    languages: tally("language"),
    channels: tally("channel_title"),
    watched: history.length,
  };
};
